/** @jsx jsx */
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { jsx } from '@emotion/core';
import { useTheme, Typography, useMediaQuery } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

import ROUTES from 'constants/routes';
import { ICustomTheme } from 'interfaces';
import {
  challengesStatusSelector,
  historySelectedChallengeSelector,
  sortedDeclaredChallengesSelector,
} from 'reducers/challenge.reducer';
import { StyledBubble } from '../styles';
import { LOADING_STATUS } from 'constants/index';

export const GoToDeclaredChallengesList = (): JSX.Element => {
  const theme: ICustomTheme = useTheme();
  return (
    <Link
      to={`${ROUTES.CHALLENGE}${ROUTES.HISTORY}`}
      css={{ position: 'relative', textDecoration: 'none' }}
    >
      <StyledBubble
        css={{
          zIndex: 1,
          position: 'absolute',
          cursor: 'pointer',
          top: '50%',
          left: '3%',
          transform: 'translateY(-50%)',
          backgroundColor: theme.colors.grey.dark,
        }}
      >
        <ArrowBackIcon fontSize="large" />
      </StyledBubble>
    </Link>
  );
};

export default function ActionsHeading(): JSX.Element | null {
  const { t } = useTranslation();
  const theme: ICustomTheme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });

  const declaredChallenges = useSelector(sortedDeclaredChallengesSelector);
  const declaredChallengesLoadingStatus = useSelector(challengesStatusSelector);
  const isChallengesOnResolvedStatus =
    declaredChallengesLoadingStatus === LOADING_STATUS.RESOLVED;
  const hasDeclaredChallenges =
    isChallengesOnResolvedStatus && !!declaredChallenges.length;

  const historySelectedChallenge = useSelector(
    historySelectedChallengeSelector,
  );
  const hasSelectedChallengeData = historySelectedChallenge?.data;

  const selectAChallengeText = t(
    'challenges.declaration.history.my_actions_hints',
  );

  if (!hasDeclaredChallenges) return null;

  return (
    <div
      css={{
        width: '100%',
        backgroundColor: theme.colors.grey.light5,
        padding: theme.spacing(2),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {isMobile && hasSelectedChallengeData && <GoToDeclaredChallengesList />}
        <Typography
          css={{
            color: theme.colors.grey.light,
            fontWeight: 500,
            width: '100%',
            [theme.breakpoints.down('sm')]: {
              textAlign: 'center',
            },
          }}
          variant="h4"
        >
          {t('challenges.declaration.history.my_actions')}
        </Typography>
      </div>
      {isMobile && !hasSelectedChallengeData && (
        <Typography
          css={{
            color: theme.colors.grey.main,
            maxWidth: '27ch',
            margin: theme.spacing(1, 'auto', 0),
          }}
          align="center"
          variant="body2"
        >
          {selectAChallengeText}
        </Typography>
      )}
    </div>
  );
}
